import { Link } from 'react-router';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { ImageWithFallback } from '@/app/components/figma/ImageWithFallback';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCategory } from '@/contexts/CategoryContext';
import { getAllProjects } from '@/services/api';
import type { Project } from '@/data/projects';
import { generateProjectUrl } from '@/utils/slug';

export function Projects() {
  const { language, t } = useLanguage();
  const { selectedCategory } = useCategory();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    async function loadProjects() {
      console.log('🔵 Projects - Starting to load...');
      setLoading(true);
      try {
        const data = await getAllProjects();
        console.log('🔵 Projects - Raw data:', data);
        // Filter out null or invalid projects
        const validProjects = data.filter(project => 
          project && 
          project.id && 
          project.title && 
          project.location && 
          project.images && 
          project.images.length > 0
        );
        console.log('🔵 Projects - Valid projects:', validProjects.length);
        setProjects(validProjects);
        setLoading(false);
        console.log('🔵 Projects - Loading complete');
      } catch (error) {
        console.error('🔴 Projects - Error loading:', error);
        setLoading(false);
      }
    }
    loadProjects();
  }, []);

  useEffect(() => {
    // Detect if device has touch capability
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    setIsTouchDevice(hasTouch);
    
    // Track window width for responsive grid
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clear saved scroll when category changes
  useEffect(() => {
    sessionStorage.removeItem('projectsScrollY');
    window.scrollTo(0, 0);
  }, [selectedCategory]);

  // Save scroll position before navigating to project detail
  const handleProjectLinkClick = () => {
    const scrollY = window.scrollY;
    console.log('Saving scroll position:', scrollY);
    sessionStorage.setItem('projectsScrollY', scrollY.toString());
  };

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  if (loading) {
    return (
      <section id="projects" className="pt-32 pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="projects" className="pt-32 pb-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6" style={{
          gridTemplateColumns: isTouchDevice 
            ? '1fr' 
            : windowWidth >= 1192 
              ? 'repeat(3, 360px)' 
              : windowWidth >= 800 
                ? 'repeat(2, 360px)' 
                : '1fr',
          justifyContent: windowWidth >= 800 && !isTouchDevice ? 'space-between' : 'center'
        }}>
          {filteredProjects.map((project, index) => {
            // Calculate column position for alignment
            const columnIndex2 = index % 2;
            const columnIndex3 = index % 3;
            
            // Determine alignment based on current layout
            let justifySelf = 'center';
            if (!isTouchDevice) {
              if (windowWidth >= 1192) {
                // 3 columns: start, center, end
                justifySelf = columnIndex3 === 0 ? 'start' : columnIndex3 === 1 ? 'center' : 'end';
              } else if (windowWidth >= 800) {
                // 2 columns: start, end
                justifySelf = columnIndex2 === 0 ? 'start' : 'end';
              }
            }
            
            return (
              <Link 
                key={project.id} 
                to={`/project/${generateProjectUrl(project)}`}
                className="group cursor-pointer w-full max-w-[360px]"
                style={{ justifySelf }}
                onClick={handleProjectLinkClick}
              >
                <div className="relative w-full aspect-[16/9] mb-2 overflow-hidden">
                  <ImageWithFallback
                    src={project.images[0]}
                    alt={project.title[language]}
                    className={`w-full h-full object-cover ${!isTouchDevice ? 'transition-transform duration-500 group-hover:scale-105' : ''}`}
                  />
                </div>
                <div className={`${isTouchDevice ? 'opacity-100' : 'md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300'} w-full h-16`}>
                  {(() => {
                    const title = project.title[language];
                    const city = project.location.split(',')[0].trim();
                    const isHungary = project.location.toLowerCase().includes('magyarország') || 
                                     project.location.toLowerCase().includes('hungary');
                    const location = isHungary ? city : project.location;
                    
                    // Check if title already contains the city name
                    const titleContainsLocation = title.toLowerCase().includes(city.toLowerCase());
                    
                    if (titleContainsLocation) {
                      return (
                        <p className="text-gray-900 uppercase tracking-wide leading-tight" style={{ fontWeight: 300, fontSize: '1.1rem' }}>
                          {title}
                        </p>
                      );
                    } else {
                      return (
                        <p className="uppercase tracking-wide leading-tight" style={{ fontWeight: 300, fontSize: '1.1rem' }}>
                          <span className="text-gray-900">{title}</span>
                          {' '}
                          <span className="text-gray-400">{location}</span>
                        </p>
                      );
                    }
                  })()}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}